import React, { useState } from 'react';
import { useLanguage, LANGUAGE_MAP } from './LanguageState';

/**
 * LanguageSelector.jsx
 * Highly customizable language selector component with 4 display variants,
 * mirroring the design of CurrencySelector.jsx.
 *
 * Variants:
 *  - 'dropdown'  → HTML-style custom dropdown
 *  - 'pills'     → Horizontal button pills
 *  - 'flags'     → Flag + language code badges
 *  - 'minimal'   → Clean text links
 *
 * Placement:
 *  - 'header-right' (default)
 *  - 'nav-inline'
 *  - 'footer'
 *  - 'floating'
 */
export const LanguageSelector = ({ settings = {}, style = {}, className = '' }) => {
    const { enabled, activeLanguage, setActiveLanguage, availableLanguages } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const [hoveredLang, setHoveredLang] = useState(null);

    // Module gate — render nothing when disabled or only one language
    if (
        !enabled ||
        settings.hideLanguageSelector ||
        settings.i18nSelectorDisabled ||
        !availableLanguages ||
        availableLanguages.length <= 1
    ) {
        return null;
    }

    // ── Read customizer settings ─────────────────────────────────────────────
    // Prefer settings-level overrides (for future Design Editor support),
    // then fall back to the context's module config values.
    const i18nMod = typeof window !== 'undefined'
        ? (() => { try { const ctx = JSON.parse(localStorage.getItem('omnivaso_cached_config') || '{}'); return ctx?.modules?.i18n || {}; } catch { return {}; } })()
        : {};

    const variant   = settings.i18nSelectorVariant   || i18nMod.selectorVariant   || 'dropdown';
    const placement = settings.i18nSelectorPlacement  || i18nMod.selectorPlacement  || 'header-right';
    const showFlags = (settings.i18nShowFlags         !== undefined ? settings.i18nShowFlags         : i18nMod.showFlags)         !== false;
    const showName  = (settings.i18nShowLanguageName  !== undefined ? settings.i18nShowLanguageName  : i18nMod.showLanguageName)  !== false;

    // ── Style tokens ─────────────────────────────────────────────────────────
    const bg         = settings.i18nSelectorBg     || '#f8fafc';
    const hoverBg    = settings.i18nSelectorHoverBg || '#e2e8f0';
    const color      = settings.i18nSelectorColor   || '#1e293b';
    const hoverColor = settings.i18nSelectorHoverColor || '#0f172a';
    const border     = settings.i18nSelectorBorder  || '1px solid #cbd5e1';
    const radius     = settings.i18nSelectorRadius  || '8px';
    const padding    = settings.i18nSelectorPadding || '4px 10px';
    const fontSize   = settings.i18nSelectorFontSize || '0.85rem';
    const fontWeight = settings.i18nSelectorFontWeight || '600';

    const activeLangInfo = LANGUAGE_MAP[activeLanguage] || LANGUAGE_MAP.en;

    // ── Placement wrapper ─────────────────────────────────────────────────────
    let wrapperStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', position: 'relative', ...style };
    if (placement === 'floating') {
        wrapperStyle = {
            position: 'fixed',
            bottom: '80px', // above currency selector if both active
            right: '24px',
            zIndex: 9998,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            background: bg,
            borderRadius: radius,
            padding: '4px',
            ...style,
        };
    }

    // ── Variant: PILLS ────────────────────────────────────────────────────────
    if (variant === 'pills') {
        return (
            <div style={wrapperStyle} className={className}>
                {availableLanguages.map(code => {
                    const info = LANGUAGE_MAP[code];
                    if (!info) return null;
                    const isActive = code === activeLanguage;
                    const isHov = hoveredLang === code;
                    return (
                        <button
                            key={code}
                            type="button"
                            title={info.name}
                            aria-label={`Switch language to ${info.name}`}
                            aria-pressed={isActive}
                            onClick={() => setActiveLanguage(code)}
                            onMouseEnter={() => setHoveredLang(code)}
                            onMouseLeave={() => setHoveredLang(null)}
                            style={{
                                background: isActive ? (settings.i18nActiveBg || '#1e293b') : (isHov ? hoverBg : bg),
                                color: isActive ? (settings.i18nActiveColor || '#ffffff') : (isHov ? hoverColor : color),
                                border,
                                borderRadius: radius,
                                padding,
                                fontSize,
                                fontWeight,
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                transition: 'background 0.18s, color 0.18s',
                                outline: 'none',
                            }}
                        >
                            {showFlags && <span aria-hidden="true">{info.flag}</span>}
                            <span>{info.code.toUpperCase()}</span>
                        </button>
                    );
                })}
            </div>
        );
    }

    // ── Variant: FLAGS ────────────────────────────────────────────────────────
    if (variant === 'flags') {
        return (
            <div style={wrapperStyle} className={className}>
                {availableLanguages.map(code => {
                    const info = LANGUAGE_MAP[code];
                    if (!info) return null;
                    const isActive = code === activeLanguage;
                    const isHov = hoveredLang === code;
                    return (
                        <button
                            key={code}
                            type="button"
                            title={info.name}
                            aria-label={`Switch language to ${info.name}`}
                            aria-pressed={isActive}
                            onClick={() => setActiveLanguage(code)}
                            onMouseEnter={() => setHoveredLang(code)}
                            onMouseLeave={() => setHoveredLang(null)}
                            style={{
                                background: isActive ? (settings.i18nActiveBg || 'rgba(0,0,0,0.15)') : 'transparent',
                                border: isActive ? border : '1px solid transparent',
                                borderRadius: radius,
                                padding: '4px 8px',
                                fontSize: '1.3rem',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                opacity: isActive ? 1 : (isHov ? 1 : 0.65),
                                transition: 'opacity 0.18s, background 0.18s',
                                outline: 'none',
                            }}
                        >
                            <span aria-hidden="true">{info.flag}</span>
                            {showName && (
                                <span style={{ fontSize: '0.75rem', fontWeight: '600', color }}>
                                    {info.code.toUpperCase()}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        );
    }

    // ── Variant: MINIMAL ──────────────────────────────────────────────────────
    if (variant === 'minimal') {
        return (
            <div style={{ ...wrapperStyle, gap: '12px' }} className={className}>
                {availableLanguages.map(code => {
                    const info = LANGUAGE_MAP[code];
                    if (!info) return null;
                    const isActive = code === activeLanguage;
                    const isHov = hoveredLang === code;
                    return (
                        <button
                            key={code}
                            type="button"
                            title={info.name}
                            aria-label={`Switch language to ${info.name}`}
                            aria-pressed={isActive}
                            onClick={() => setActiveLanguage(code)}
                            onMouseEnter={() => setHoveredLang(code)}
                            onMouseLeave={() => setHoveredLang(null)}
                            style={{
                                background: 'none',
                                border: 'none',
                                borderBottom: isActive ? `2px solid ${color}` : '2px solid transparent',
                                padding: '2px 0',
                                color: isActive || isHov ? color : `${color}99`,
                                fontSize,
                                fontWeight: isActive ? '700' : fontWeight,
                                cursor: 'pointer',
                                transition: 'color 0.18s, border-bottom-color 0.18s',
                                outline: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                            }}
                        >
                            {showFlags && <span aria-hidden="true">{info.flag}</span>}
                            <span>{info.code.toUpperCase()}</span>
                        </button>
                    );
                })}
            </div>
        );
    }

    // ── Variant: DROPDOWN (default) ───────────────────────────────────────────
    return (
        <div style={{ ...wrapperStyle, position: 'relative' }} className={className}>
            <button
                type="button"
                id="omnivaso-lang-selector"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={`Language: ${activeLangInfo.name}`}
                onClick={() => setIsOpen(o => !o)}
                onBlur={(e) => {
                    if (!e.currentTarget.parentElement.contains(e.relatedTarget)) {
                        setIsOpen(false);
                    }
                }}
                style={{
                    background: bg,
                    color,
                    border,
                    borderRadius: radius,
                    padding,
                    fontSize,
                    fontWeight,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap',
                    transition: 'background 0.18s',
                    outline: 'none',
                }}
            >
                {showFlags && <span aria-hidden="true">{activeLangInfo.flag}</span>}
                <span>{activeLangInfo.code.toUpperCase()}</span>
                <span style={{ fontSize: '0.7em', opacity: 0.7, marginLeft: '2px' }}>▾</span>
            </button>

            {isOpen && (
                <div
                    role="listbox"
                    aria-label="Available languages"
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        right: 0,
                        zIndex: 10010,
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '10px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.13)',
                        padding: '6px',
                        minWidth: '160px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                    }}
                >
                    {availableLanguages.map(code => {
                        const info = LANGUAGE_MAP[code];
                        if (!info) return null;
                        const isActive = code === activeLanguage;
                        const isHov = hoveredLang === code;
                        return (
                            <button
                                key={code}
                                type="button"
                                role="option"
                                aria-selected={isActive}
                                onClick={() => { setActiveLanguage(code); setIsOpen(false); }}
                                onMouseEnter={() => setHoveredLang(code)}
                                onMouseLeave={() => setHoveredLang(null)}
                                style={{
                                    background: isActive ? '#f0f9ff' : (isHov ? '#f8fafc' : 'transparent'),
                                    color: isActive ? '#0369a1' : '#1e293b',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '8px 12px',
                                    fontSize: '0.87rem',
                                    fontWeight: isActive ? '700' : '500',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    textAlign: 'left',
                                    width: '100%',
                                    transition: 'background 0.14s',
                                    outline: 'none',
                                }}
                            >
                                {showFlags && <span aria-hidden="true" style={{ fontSize: '1.1rem' }}>{info.flag}</span>}
                                <span style={{ flex: 1 }}>{info.nativeName}</span>
                                {isActive && <span aria-hidden="true" style={{ color: '#0369a1', fontSize: '0.9em' }}>✓</span>}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default LanguageSelector;
